import { useCallback, useEffect, useRef, useState } from 'react';
import { Location } from 'history';
import { useHistory } from 'react-router-dom';

interface UseUnsavedNavigationGuardOptions {
  isDirty: boolean;
  onSave: () => boolean | void | Promise<boolean | void>;
  onDiscard?: () => void | Promise<void>;
}

export const useUnsavedNavigationGuard = ({ isDirty, onSave, onDiscard }: UseUnsavedNavigationGuardOptions) => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const nextLocationRef = useRef<Location | null>(null);
  const nextActionRef = useRef<(() => void) | null>(null);
  const allowNavigationRef = useRef(false);

  const proceed = useCallback(() => {
    const nextAction = nextActionRef.current;
    if (nextAction) {
      nextActionRef.current = null;
      setIsOpen(false);
      nextAction();
      return;
    }

    const nextLocation = nextLocationRef.current;
    if (!nextLocation) return;

    allowNavigationRef.current = true;
    nextLocationRef.current = null;
    setIsOpen(false);
    history.push(nextLocation);
    setTimeout(() => {
      allowNavigationRef.current = false;
    }, 0);
  }, [history]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const unblock = history.block((nextLocation) => {
      if (allowNavigationRef.current) return undefined;
      nextLocationRef.current = nextLocation;
      setIsOpen(true);
      return 'true';
    });

    return unblock;
  }, [history, isDirty]);

  const saveAndProceed = useCallback(async () => {
    setIsSaving(true);
    try {
      const succeeded = await onSave();
      if (succeeded === true) {
        proceed();
      }
    } finally {
      setIsSaving(false);
    }
  }, [onSave, proceed]);

  const discardAndProceed = useCallback(async () => {
    await onDiscard?.();
    proceed();
  }, [onDiscard, proceed]);

  const stay = useCallback(() => {
    nextLocationRef.current = null;
    nextActionRef.current = null;
    setIsOpen(false);
  }, []);

  const requestNavigation = useCallback(
    (action: () => void) => {
      if (!isDirty) {
        action();
        return;
      }
      nextActionRef.current = action;
      setIsOpen(true);
    },
    [isDirty],
  );

  return {
    isOpen,
    isSaving,
    saveAndProceed,
    discardAndProceed,
    stay,
    requestNavigation,
    proceedAfterSave: proceed,
  };
};
